import { dollars, dollarsCompact, pct } from "../lib/format";
import type { OpenPosition } from "../lib/types";

type Props = {
  positions: OpenPosition[] | null;
  asOf?: string;
};

/**
 * Open positions snapshot as of the latest NAV row.
 *
 * The bot sends the position book inline on every NAV push (added in
 * trader-bot commit 546b7e3). Older rows have `open_positions === null`
 * — we render an explanatory empty state rather than hide the panel.
 *
 * Each row shows: symbol, qty, entry vs current, market value,
 * unrealized P&L (colored green/red), and side.
 */
export function OpenPositionsPanel({ positions, asOf }: Props) {
  const hasData = positions != null && positions.length > 0;
  const totalValue = hasData
    ? positions.reduce((s, p) => s + p.market_value_cents, 0)
    : 0;
  const totalPnl = hasData
    ? positions.reduce((s, p) => s + p.unrealized_pl_cents, 0)
    : 0;

  return (
    <section className="rounded-lg border border-ink-700 bg-ink-900">
      <div className="flex items-baseline justify-between border-b border-ink-700 px-3 py-2 text-sm">
        <div>
          <span className="font-semibold text-slate-200">Open positions</span>
          {hasData && (
            <span className="ml-2 text-xs font-normal text-slate-500">
              ({positions.length})
            </span>
          )}
        </div>
        {asOf && (
          <div className="font-mono text-xs text-slate-500">
            as of {asOf}
          </div>
        )}
      </div>
      {!hasData ? (
        <div className="px-3 py-6 text-center text-sm text-slate-500">
          {positions == null
            ? "No position snapshot in the latest NAV row."
            : "No open positions."}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-ink-700 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">symbol</th>
                  <th className="px-3 py-2 text-right font-medium">qty</th>
                  <th className="px-3 py-2 text-right font-medium">entry</th>
                  <th className="px-3 py-2 text-right font-medium">current</th>
                  <th className="px-3 py-2 text-right font-medium">value</th>
                  <th className="px-3 py-2 text-right font-medium">P&amp;L</th>
                  <th className="px-3 py-2 text-right font-medium">%</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => {
                  const pnlPositive = p.unrealized_pl_cents >= 0;
                  const pnlClass = pnlPositive
                    ? "text-accent-ok"
                    : "text-accent-err";
                  return (
                    <tr
                      key={`${p.symbol}-${p.side}`}
                      className="border-b border-ink-700/50 last:border-b-0"
                    >
                      <td className="px-3 py-2 font-mono font-medium text-slate-100">
                        {p.symbol}
                        {p.side !== "long" && (
                          <span className="ml-2 rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">
                            {p.side}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">
                        {p.qty}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-400">
                        {dollars(p.avg_entry_price_cents)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">
                        {dollars(p.current_price_cents)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-200">
                        {dollarsCompact(p.market_value_cents)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono ${pnlClass}`}
                      >
                        {pnlPositive ? "+" : ""}
                        {dollarsCompact(p.unrealized_pl_cents)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono ${pnlClass}`}
                      >
                        {pct(p.unrealized_plpc)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-ink-700 bg-ink-800/30">
                  <td
                    colSpan={4}
                    className="px-3 py-2 text-right text-xs uppercase tracking-wider text-slate-500"
                  >
                    total
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-medium text-slate-100">
                    {dollarsCompact(totalValue)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-mono font-medium ${
                      totalPnl >= 0 ? "text-accent-ok" : "text-accent-err"
                    }`}
                  >
                    {totalPnl >= 0 ? "+" : ""}
                    {dollarsCompact(totalPnl)}
                  </td>
                  <td className="px-3 py-2" />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
